import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function ProjectInfo() {
  const [formData, setFormData] = useState({
    projectName: '',
    projectType: '',
    projectStartDate: '',
    projectEndDate: '',
    estimatedFunding: '',
    projectSummary: '',
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();
  const activeStep = 3;
  const steps = [
    { number: 1, title: 'Upload Document', description: 'Attach your application file' },
    { number: 2, title: 'Applicant Information', description: 'Enter contact and address details' },
    { number: 3, title: 'Project Information', description: 'Describe the project and funding needs' },
    { number: 4, title: 'Budget', description: 'Provide budget allocations' },
    { number: 5, title: 'DOL Determination', description: 'Complete final eligibility review' },
  ];

  useEffect(() => {
    const storedData = sessionStorage.getItem('newApplicationData');
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.projectInfo) {
          setFormData(parsed.projectInfo);
        }
      } catch {
        sessionStorage.removeItem('newApplicationData');
      }
    }
  }, []);

  const handleChange = (event) => {
    setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.projectName.trim()) nextErrors.projectName = 'Project name is required.';
    if (!formData.projectType.trim()) nextErrors.projectType = 'Project type is required.';
    if (!formData.projectStartDate.trim()) nextErrors.projectStartDate = 'Start date is required.';
    if (!formData.projectEndDate.trim()) nextErrors.projectEndDate = 'End date is required.';
    if (!formData.estimatedFunding.trim()) nextErrors.estimatedFunding = 'Estimated funding is required.';
    if (!formData.projectSummary.trim()) nextErrors.projectSummary = 'Project summary is required.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleContinue = (event) => {
    event.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    const currentData = JSON.parse(sessionStorage.getItem('newApplicationData') || '{}');
    sessionStorage.setItem('newApplicationData', JSON.stringify({
      ...currentData,
      projectInfo: { ...formData },
    }));
    navigate('/dashboard/new-application/budget');
  };

  return (
    <div className="page-stack">
        <section className="app-flow-header">
          <div>
            <p className="app-flow-tag">Application Flow</p>
            <h1>Project Information</h1>
            <p className="app-flow-description">
              Provide the core details for the proposed project, including timelines and funding projections.
            </p>
          </div>
          <div className="step-badges">
            {steps.map((step) => (
              <div key={step.number} className={`step-item ${activeStep === step.number ? 'active' : ''}`}>
                <div className="step-number">{step.number}</div>
                <div className="step-text">
                  <div className="step-title">{step.title}</div>
                  <div className="step-desc">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="application-content">
          <div className="form-card">
            <h2 className="section-heading">Step 3: Project Information</h2>
            <p className="form-description">
              Outline the project scope and estimated funding requirements in this section.
            </p>
            <form onSubmit={handleContinue} noValidate>
              <label className="form-field">
                <span>Project Name</span>
                <input
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleChange}
                  className={errors.projectName ? 'input-error' : ''}
                  placeholder="Enter project name"
                />
                {errors.projectName && <div className="field-error">{errors.projectName}</div>}
              </label>
              <label className="form-field">
                <span>Project Type</span>
                <input
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className={errors.projectType ? 'input-error' : ''}
                  placeholder="e.g. Infrastructure, Training, Research"
                />
                {errors.projectType && <div className="field-error">{errors.projectType}</div>}
              </label>
              <div className="form-row">
                <label className="form-field">
                  <span>Start Date</span>
                  <input
                    type="date"
                    name="projectStartDate"
                    value={formData.projectStartDate}
                    onChange={handleChange}
                    className={errors.projectStartDate ? 'input-error' : ''}
                  />
                  {errors.projectStartDate && <div className="field-error">{errors.projectStartDate}</div>}
                </label>
                <label className="form-field">
                  <span>End Date</span>
                  <input
                    type="date"
                    name="projectEndDate"
                    value={formData.projectEndDate}
                    onChange={handleChange}
                    className={errors.projectEndDate ? 'input-error' : ''}
                  />
                  {errors.projectEndDate && <div className="field-error">{errors.projectEndDate}</div>}
                </label>
              </div>
              <label className="form-field">
                <span>Estimated Funding Amount</span>
                <input
                  name="estimatedFunding"
                  value={formData.estimatedFunding}
                  onChange={handleChange}
                  className={errors.estimatedFunding ? 'input-error' : ''}
                  placeholder="e.g. $120,000"
                />
                {errors.estimatedFunding && <div className="field-error">{errors.estimatedFunding}</div>}
              </label>
              <label className="form-field">
                <span>Project Summary</span>
                <textarea
                  name="projectSummary"
                  value={formData.projectSummary}
                  onChange={handleChange}
                  className={errors.projectSummary ? 'input-error' : ''}
                  placeholder="Brief description of the project and expected outcomes"
                  rows={5}
                />
                {errors.projectSummary && <div className="field-error">{errors.projectSummary}</div>}
              </label>
              {submitError && <div className="section-error">{submitError}</div>}
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => navigate('/dashboard/new-application')}>
                  Back to Applicant Info
                </button>
                <button type="submit" className="submit-btn">
                  Save and Continue to Budget
                </button>
              </div>
            </form>
          </div>
        </section>
    </div>
  );
}

export default ProjectInfo;
